<template>
  <div class="main-container">
    <div class="hero">
      <div class="hero-meta">
        <span class="company-chip">{{ $route.params.company }}</span>
        <span class="meta-dot">•</span>
        <span class="meta-text">ESG</span>
      </div>
      <h1 class="title">ESG</h1>
      <p class="subtitle">Acompanhe indicadores e projetos ESG.</p>
    </div>
    <div class="all-cards">
      <template v-for="submodule in displayedSubmodules" :key="submodule">
        <a :href="submoduleLinks[submodule]">
          <div class="card">
            <img :alt="submoduleNames[submodule]" :src="icons[submodule]">
            <h1>{{ submoduleNames[submodule] }}</h1>
          </div>
        </a>
      </template>


    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getFirestore } from 'firebase/firestore';
import { getUserSubmodulesCached } from '@/utils/moduleSubmodulesCache';
import { recordPerfMetric } from '@/utils/perfTelemetry';

export default {
  name: "ESG",
  data() {
    return {
      submodules: [],
      icons: {
        "diagnostico_esg": require('@/assets/balanca_icon.png'),
        "diagnosis": require('@/assets/glass_icon.png'),
        "ambiental": require('@/assets/siga_icon.png'),
        "plan": require('@/assets/checklist_icon.png'),
        "kpi": require('@/assets/positive-chart_icon.png'),
        "inventario": require('@/assets/inventory_icon.png'),
        "descarbonizacao": require('@/assets/descarbonizacao_icon.png')
      },
      submoduleNames: {
        "diagnostico_esg": "Matriz de materialidade",
        "diagnosis": "Diagnóstico ESG",
        "ambiental": "Gestão Ambiental",
        "plan": "Plano de ação",
        "kpi": "KPI de Indicadores de Performance Sustentável",
        "inventario": "Inventário de GEE",
        "descarbonizacao": "Plano de Descarbonização"
      },
      submoduleLinks: {
        "diagnostico_esg": this.getModuleUrl('matriz-de-materialidade'),
        "diagnosis": this.getModuleUrl('diagnosis'),
        "ambiental": this.getModuleUrl('ambiental'),
        "plan": this.getModuleUrl('plan'),
        "kpi": this.getModuleUrl('kpi'),
        "inventario": this.getModuleUrl('inventary'),
        "descarbonizacao": this.getModuleUrl('descarbonizacao')
      }
    };
  },
  computed: {
    ...mapGetters(['email']),
    displayedSubmodules() {
      if (this.submodules.length > 0) {
        return this.submodules;
      }
      return Object.keys(this.submoduleNames);
    }
  },
  async mounted() {
    if (this.email) {
      await this.loadUserSubmodules();
    }
  },
  methods: {
    async loadUserSubmodules() {
      const startedAt = Date.now();
      const db = getFirestore();
      try {
        this.submodules = await getUserSubmodulesCached(
          db,
          this.$route.params.company,
          this.email,
          'esg'
        );
        recordPerfMetric('esg_home', 'load_submodules', Date.now() - startedAt, {
          total: this.submodules.length,
        });
      } catch (error) {
        this.submodules = [];
        recordPerfMetric('esg_home', 'load_submodules_error', Date.now() - startedAt, {
          code: error?.code || 'unknown',
        });
      }
    },
    getModuleUrl(modulePath) {
      return `/${this.$route.params.company}/esg/${modulePath}/`;
    }
  }
};
</script>

<style scoped>
a { color: var(--color-text); text-decoration: none; }
.main-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 24px 16px 32px;
}
.main-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 220px;
  background: linear-gradient(135deg, rgba(8, 131, 149, 0.12), rgba(10, 34, 43, 0.06));
  border-radius: 0 0 32px 32px;
  z-index: 0;
}
.hero {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 32px;
}
.hero-meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  color: var(--color-muted);
  font-size: var(--font-muted-size);
  margin-bottom: 16px;
}
.company-chip {
  color: var(--color-title);
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}
.meta-dot { color: var(--color-muted); }
.meta-text { color: var(--color-muted); }
.title {
  color: var(--color-title);
  font-family: var(--font-title-family);
  font-weight: 700;
  font-size: 36px;
  margin: 0 0 8px;
}
.subtitle {
  margin: 0;
  font-size: 16px;
  color: var(--color-muted);
}
.all-cards {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1100px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  padding: 16px 0 40px;
  margin-bottom: 20px;
  background: transparent;
  color: var(--color-text);
  font-family: var(--font-text-family);
}
.card {
  width: 100%;
  min-height: 220px;
  background: var(--color-surface);
  color: var(--color-text);
  border-radius: 18px;
  border: 1px solid var(--color-border);
  padding: 22px 20px;
  box-shadow: 0 12px 24px rgba(10, 34, 43, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}
.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 30px rgba(10, 34, 43, 0.12);
}
.card img {
  width: 60px;
  height: 60px;
  margin-bottom: 18px;
}
.card h1 {
  font-family: var(--font-title-family);
  font-weight: 700;
  font-size: 20px;
  margin-bottom: 8px;
  color: var(--color-title);
}
.card p {
  font-family: var(--font-text-family);
  font-size: 14px;
  color: var(--color-muted);
  margin: 0;
}
</style>


